import { ExclamationTriangleIcon } from '@radix-ui/react-icons';
import {
  Blockquote,
  Box,
  Button,
  Callout,
  Flex,
  Heading,
  Text,
} from '@radix-ui/themes';
import { t } from 'i18next';
import { IMenuAction } from 'interfaces/i-menus';
import { StringHelper } from 'lib_ts/classes/string.helper';
import {
  RADIX,
  RadixCalloutSize,
  RadixCalloutVariant,
  RadixColor,
  RadixFlexAlign,
  RadixFlexJustify,
  RadixTextSize,
} from 'lib_ts/enums/radix-ui';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import './index.scss';

interface IProps {
  size?: RadixCalloutSize;

  flexAlign?: RadixFlexAlign;
  flexJustify?: RadixFlexJustify;

  header?: string;

  /** will be translated and rendered via Callout.Text */
  text?: string;
  /** will be translated and rendered via react markdown in a box */
  text_md?: string;
  content?: ReactNode;
  icon?: ReactNode;
  color?: RadixColor;
  variant?: RadixCalloutVariant;
  hideIcon?: boolean;

  actions?: IMenuAction[];
  actionsDirection?: 'row' | 'column';

  autoDismissMS?: number;

  style?: React.CSSProperties;
}

export const CommonCallout = (props: IProps) => {
  const [autoDismissed, setAutoDismissed] = useState(false);

  useEffect(() => {
    if (props.autoDismissMS === undefined) {
      return;
    }

    setTimeout(() => {
      setAutoDismissed(true);
    }, props.autoDismissMS);
  }, [props.autoDismissMS]);

  const padding = useMemo(() => {
    switch (props.size) {
      case '1': {
        return '3';
      }

      case '3': {
        return '5';
      }

      case '2':
      default: {
        return '4';
      }
    }
  }, [props.size]);

  const fontSize = useMemo((): RadixTextSize => {
    switch (props.size) {
      case '1': {
        return '1';
      }

      case '3': {
        return '3';
      }

      case '2':
      default: {
        return '2';
      }
    }
  }, [props.size]);

  const headingSize = useMemo((): RadixTextSize => {
    switch (props.size) {
      case '1': {
        return RADIX.HEADING.SIZE.XS;
      }

      case '3': {
        return '3';
      }

      case '2':
      default: {
        return RADIX.HEADING.SIZE.SM;
      }
    }
  }, [props.size]);

  const renderIcon = (textSize: RadixTextSize) => (
    <Text size={textSize} color={props.color} className="CalloutIcon">
      <Flex align="center" justify="center" className="CalloutIcon__inner">
        {props.icon ?? <ExclamationTriangleIcon />}
      </Flex>
    </Text>
  );

  const iconBesideHeader = !props.hideIcon && !!props.header;
  const iconBesideBody = !props.hideIcon && !props.header;

  if (autoDismissed) {
    return null;
  }

  return (
    <Callout.Root
      className="bg-background"
      variant={props.variant ?? 'outline'}
      color={RADIX.COLOR.NEUTRAL}
      style={{
        padding: 0,
        overflow: 'hidden',
        ...props.style,
      }}
    >
      <Blockquote
        className="CalloutBlockquote"
        color={props.color ?? RADIX.COLOR.WARNING}
        style={{ padding: 0 }}
      >
        <Flex
          p={padding}
          gap="2"
          align={props.flexAlign ?? 'center'}
          justify={props.flexJustify}
          style={{ width: '100%' }}
        >
          {iconBesideBody && renderIcon(fontSize)}

          <Flex
            flexGrow="1"
            direction="column"
            gap={RADIX.FLEX.GAP.SM}
            justify="start"
            style={{ color: RADIX.COLOR.SAFE_TEXT }}
          >
            {props.header && (
              <Flex align="center" gap="2">
                {iconBesideHeader && renderIcon(headingSize)}
                <Heading size={headingSize}>{t(props.header)}</Heading>
              </Flex>
            )}

            {props.text && <Text size={fontSize}>{t(props.text)}</Text>}

            {props.text_md && (
              <Text size={fontSize}>
                <ReactMarkdown>{t(props.text_md)}</ReactMarkdown>
              </Text>
            )}

            {props.content}
          </Flex>

          {props.actions && (
            <Flex
              direction={props.actionsDirection ?? 'column'}
              gap={RADIX.FLEX.GAP.SM}
              align="center"
              justify="center"
              style={{ alignSelf: 'center' }}
            >
              {props.actions
                .filter((a) => !a.invisible)
                .map((a, i) => (
                  <Box key={i}>
                    <Button
                      size={a.size ?? '1'}
                      title={a.tooltip}
                      disabled={a.disabled}
                      color={a.color ?? props.color ?? RADIX.COLOR.NEUTRAL}
                      variant={a.variant ?? 'soft'}
                      className={StringHelper.classNames([
                        'btn-block',
                        a.className,
                      ])}
                      onClick={a.onClick}
                    >
                      {a.prefixIcon}
                      {t(a.label)}
                      {a.suffixIcon}
                    </Button>
                  </Box>
                ))}
            </Flex>
          )}
        </Flex>
      </Blockquote>
    </Callout.Root>
  );
};
